import { useQuery } from "@tanstack/react-query";
import {
  dependencyDocumentsApi,
  type DependencyDocumentMeta,
  type DependencyDocumentWithContent,
} from "@/lib/api/dependency-documents";

/**
 * Query key factory for precursor ("dependency") documents.
 *
 * `lists()` is the shared prefix both parent types hang off, so a single
 * `invalidateQueries({ queryKey: dependencyDocumentKeys.lists() })` covers the
 * Epic Request panel and the Epic panel at once.
 */
export const dependencyDocumentKeys = {
  all: ["dependency-documents"] as const,
  lists: () => [...dependencyDocumentKeys.all, "list"] as const,
  epicRequestList: (id: string) =>
    [...dependencyDocumentKeys.lists(), "epic-request", id] as const,
  epicList: (id: string) => [...dependencyDocumentKeys.lists(), "epic", id] as const,
  details: () => [...dependencyDocumentKeys.all, "detail"] as const,
  detail: (depId: string) => [...dependencyDocumentKeys.details(), depId] as const,
};

/**
 * List the precursor documents attached to an Epic Request. Metadata only.
 */
export function useEpicRequestDependencies(id: string) {
  return useQuery({
    queryKey: dependencyDocumentKeys.epicRequestList(id),
    queryFn: () => dependencyDocumentsApi.listForEpicRequest(id),
    enabled: !!id,
    select: (response): DependencyDocumentMeta[] => response.data,
  });
}

/**
 * List the precursor documents attached to an Epic. Metadata only.
 */
export function useEpicDependencies(id: string) {
  return useQuery({
    queryKey: dependencyDocumentKeys.epicList(id),
    queryFn: () => dependencyDocumentsApi.listForEpic(id),
    enabled: !!id,
    select: (response): DependencyDocumentMeta[] => response.data,
  });
}

/**
 * Fetch one document including its text content.
 *
 * Gated behind `enabled` because the list endpoints deliberately omit the
 * content column: the body is only pulled when the preview dialog opens.
 */
export function useDependencyDocument(
  depId: string | null,
  options: { enabled?: boolean } = {}
) {
  const { enabled = true } = options;
  // Normalised once so the queryFn closes over a plain string; the query never
  // runs while it is empty because `enabled` requires a non-empty depId.
  const resolvedId = depId ?? "";

  return useQuery({
    queryKey: dependencyDocumentKeys.detail(resolvedId),
    queryFn: () => dependencyDocumentsApi.get(resolvedId),
    enabled: enabled && !!resolvedId,
    select: (response): DependencyDocumentWithContent => response.data,
  });
}
